import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useBetBuilder, useBetTracker, type SavedBet, type SavedBuilderItem, type TrackedBetSlip } from '../context/BetContext'
import { useOdds } from '../context/OddsContext'
import { OddsInput } from './OddsInput'
import { BookmakerLinksButton } from './BookmakerLinksButton'
import { formatBetSlip, copyToClipboard, shareViaWebShare } from '../utils/shareUtils'
import { calculateCombinedOdds, angleMarketToOddsMarket } from '../utils'

// Check if Clerk is configured
const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY

function getConfidenceStyle(confidence: string) {
  switch (confidence) {
    case 'high':
      return { background: 'rgba(74, 222, 128, 0.15)', color: 'var(--green)' }
    case 'med':
      return { background: 'rgba(251, 191, 36, 0.15)', color: 'var(--yellow)' }
    default:
      return { background: 'rgba(251, 113, 133, 0.15)', color: 'var(--red)' }
  }
}

// Group bets by fixture, maintaining insertion order of fixtures
function groupBetsByFixture(bets: SavedBet[]): Map<string, SavedBet[]> {
  const grouped = new Map<string, SavedBet[]>()
  for (const bet of bets) {
    const existing = grouped.get(bet.fixture)
    if (existing) {
      existing.push(bet)
    } else {
      grouped.set(bet.fixture, [bet])
    }
  }
  return grouped
}

function BetItem({
  bet,
  onRemove,
  showFixture = true,
  odds,
  formatOdds,
}: {
  bet: SavedBet
  onRemove: () => void
  showFixture?: boolean
  odds?: number | null
  formatOdds?: (odds: number) => string
}) {
  const confStyle = getConfidenceStyle(bet.confidence)

  // Calculate implied probability from odds for tooltip
  const impliedProb = odds ? (100 / odds).toFixed(1) : null

  return (
    <div className="bet-item">
      <div className="bet-item-content">
        {showFixture && <div className="bet-item-fixture">{bet.fixture}</div>}
        <div className="bet-item-title">{bet.title}</div>
        <div className="bet-item-line">
          <span
            className="bet-item-confidence"
            style={confStyle}
            title={`Our calculated probability: ${bet.confidencePct}% chance this bet wins`}
          >
            {bet.confidencePct}%
          </span>
          {odds && formatOdds && (
            <span
              className="bet-item-odds"
              title={`Odds: ${odds.toFixed(2)} decimal (implies ${impliedProb}% chance)`}
            >
              {formatOdds(odds)}
            </span>
          )}
        </div>
      </div>
      <button className="trash-btn" onClick={onRemove} title="Remove from bet builder">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
    </div>
  )
}

function GroupedBetList({
  bets,
  removeBet,
  getOddsForBet,
  formatOdds,
}: {
  bets: SavedBet[]
  removeBet: (id: string) => void
  getOddsForBet?: (bet: SavedBet) => number | null
  formatOdds?: (odds: number) => string
}) {
  const groupedBets = useMemo(() => groupBetsByFixture(bets), [bets])

  return (
    <div className="bet-list grouped">
      {Array.from(groupedBets.entries()).map(([fixture, fixtureBets]) => (
        <div key={fixture} className="bet-group">
          <div className="bet-group-header">{fixture}</div>
          {fixtureBets.map((bet) => (
            <BetItem
              key={bet.id}
              bet={bet}
              onRemove={() => removeBet(bet.id)}
              showFixture={false}
              odds={getOddsForBet?.(bet)}
              formatOdds={formatOdds}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function BetBuilderPanel() {
  const {
    bets,
    removeBet,
    clearAll,
    isModalOpen,
    closeModal,
    savedBuilders,
    isSaving,
    isLoadingSaved,
    saveCurrentBuilder,
    loadSavedBuilder,
    deleteSavedBuilderById,
  } = useBetBuilder()
  const { addTrackedBet } = useBetTracker()
  const { getOddsForBookmaker, preferredBookmaker, formatOdds, oddsHidden } = useOdds()
  const auth = clerkEnabled ? useAuth() : { isSignedIn: false }
  const { isSignedIn } = auth
  const [showOddsInput, setShowOddsInput] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showSavedBuilders, setShowSavedBuilders] = useState(false)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) closeModal()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isModalOpen, closeModal])

  // Helper to get odds for a single bet (with fallback to stored odds)
  const getOddsForBet = useMemo(() => {
    if (oddsHidden) return undefined
    return (bet: SavedBet): number | null => {
      // If bet has stored odds (e.g., from value bets page), use them directly
      // Must be a valid number > 1 (decimal odds are always > 1)
      if (typeof bet.odds === 'number' && bet.odds > 1) {
        return bet.odds
      }
      // Otherwise try to get from OddsContext (for fixture page bets)
      if (preferredBookmaker) {
        const oddsMarketSlug = angleMarketToOddsMarket(bet.marketType, bet.teamType)
        if (oddsMarketSlug) {
          const outcome = bet.isOver ? 'over' : 'under'
          const contextOdds = getOddsForBookmaker(
            bet.fixtureId,
            oddsMarketSlug,
            outcome,
            preferredBookmaker.slug,
            bet.line
          )
          if (contextOdds !== null && contextOdds > 1) return contextOdds
        }
      }
      return null
    }
  }, [preferredBookmaker, getOddsForBookmaker, oddsHidden])

  // Calculate combined odds from all bets
  const combinedOddsInfo = useMemo(() => {
    if (bets.length === 0) return null
    if (!getOddsForBet) return null

    const oddsValues: number[] = []
    let allOddsAvailable = true

    for (const bet of bets) {
      const odds = getOddsForBet(bet)
      if (odds !== null) {
        oddsValues.push(odds)
      } else {
        allOddsAvailable = false
      }
    }

    if (oddsValues.length === 0) return null

    const combined = calculateCombinedOdds(oddsValues)
    return {
      combined,
      count: oddsValues.length,
      total: bets.length,
      allAvailable: allOddsAvailable,
    }
  }, [bets, getOddsForBet])

  const handleTrackSlip = () => {
    setShowOddsInput(true)
  }

  const handleShare = async () => {
    const text = formatBetSlip(bets)
    // Try Web Share API first (mobile)
    if (!shareViaWebShare(text)) {
      // Fall back to clipboard copy
      const success = await copyToClipboard(text)
      if (success) {
        setCopySuccess(true)
        setTimeout(() => setCopySuccess(false), 2000)
      }
    }
  }

  const handleSaveBuilder = async () => {
    try {
      await saveCurrentBuilder()
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
    } catch (err) {
      console.error('Failed to save builder:', err)
    }
  }

  const handleLoadBuilder = (builder: SavedBuilderItem) => {
    loadSavedBuilder(builder)
    setShowSavedBuilders(false)
  }

  const handleDeleteBuilder = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    try {
      await deleteSavedBuilderById(id)
    } catch (err) {
      console.error('Failed to delete builder:', err)
    }
  }

  const handleTrackSubmit = (oddsDecimal: number, stake: number) => {
    const trackedSlip: TrackedBetSlip = {
      id: `slip-${Date.now()}`,
      selections: [...bets],
      oddsDecimal,
      stake,
      status: 'pending',
      placedAt: new Date().toISOString(),
    }

    addTrackedBet(trackedSlip)
    clearAll()
    setShowOddsInput(false)
  }

  return (
    <>
      <div className={`bet-builder-panel ${isModalOpen ? 'open' : ''}`}>
        <div className="card">
          <div className="card-inner">
            <div className="section-title" style={{ marginBottom: 12 }}>
              <div className="row">
                <div className="h1">My Bet Builder</div>
                <span className="bet-count">{bets.length}</span>
              </div>
              <div className="row">
                {bets.length > 0 && (
                  <button className="clear-btn" onClick={clearAll} title="Clear all">
                    Clear
                  </button>
                )}
                <button className="panel-close" onClick={closeModal} title="Minimize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="bet-builder-body">
              {showSavedBuilders ? (
                // Saved builders list view
                <div className="saved-builders-list">
                  <div className="saved-builders-header">
                    <span>Saved Builders</span>
                    <button
                      className="saved-builders-close"
                      onClick={() => setShowSavedBuilders(false)}
                    >
                      &times;
                    </button>
                  </div>
                  {isLoadingSaved ? (
                    <div className="empty-state">
                      <div className="empty-text">Loading...</div>
                    </div>
                  ) : savedBuilders.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-text">No saved builders yet</div>
                    </div>
                  ) : (
                    <div className="saved-builders-items">
                      {savedBuilders.map((builder) => (
                        <div
                          key={builder.id}
                          className={`saved-builder-item ${builder.isInvalid ? 'invalid' : ''}`}
                          onClick={() => handleLoadBuilder(builder)}
                        >
                          <div className="saved-builder-info">
                            <div className="saved-builder-name">
                              {builder.name || `Builder ${builder.id}`}
                              {builder.isInvalid && (
                                <span
                                  className="saved-builder-warning"
                                  title={`Unavailable: ${builder.invalidatedPlayers.map((p) => p.playerName).join(', ')}`}
                                >
                                  ⚠️
                                </span>
                              )}
                            </div>
                            <div className="saved-builder-meta">
                              {builder.selections.length} pick{builder.selections.length !== 1 ? 's' : ''} · {new Date(builder.createdAt).toLocaleDateString()}
                              {builder.isInvalid && (
                                <span className="saved-builder-invalid-count">
                                  · {builder.invalidatedPlayers.length} unavailable
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            className="saved-builder-delete"
                            onClick={(e) => handleDeleteBuilder(e, builder.id)}
                            title="Delete"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : bets.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <div className="empty-text">
                    No picks yet.<br />
                    Add angles from fixtures to build your bet slip.
                  </div>
                  {isSignedIn && savedBuilders.length > 0 && (
                    <button
                      className="load-builder-btn"
                      onClick={() => setShowSavedBuilders(true)}
                    >
                      Load Saved ({savedBuilders.length})
                    </button>
                  )}
                </div>
              ) : (
                <GroupedBetList
                  bets={bets}
                  removeBet={removeBet}
                  getOddsForBet={getOddsForBet}
                  formatOdds={formatOdds}
                />
              )}
            </div>

            {bets.length > 0 && !showSavedBuilders && (
              <>
                {!oddsHidden && combinedOddsInfo && (
                  <div
                    className="combined-odds"
                    title={`Combined accumulator odds: ${combinedOddsInfo.combined.toFixed(2)} decimal (implies ${(100 / combinedOddsInfo.combined).toFixed(2)}% chance)`}
                  >
                    <span className="combined-odds-label">
                      Acca Odds ({combinedOddsInfo.count}/{combinedOddsInfo.total})
                    </span>
                    <span className="combined-odds-value">
                      {formatOdds(combinedOddsInfo.combined)}
                    </span>
                  </div>
                )}
                <div className="bet-builder-footer">
                  <div className="bet-builder-actions">
                    <button className="share-btn" onClick={handleShare} title={copySuccess ? 'Copied!' : 'Copy bet slip'}>
                      {copySuccess ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                          <polyline points="16 6 12 2 8 6" />
                          <line x1="12" y1="2" x2="12" y2="15" />
                        </svg>
                      )}
                    </button>
                    {isSignedIn && (
                      <>
                        <button
                          className="save-builder-btn"
                          onClick={handleSaveBuilder}
                          disabled={isSaving}
                          title={saveSuccess ? 'Saved!' : 'Save builder'}
                        >
                          {saveSuccess ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                              <polyline points="17 21 17 13 7 13 7 21" />
                              <polyline points="7 3 7 8 15 8" />
                            </svg>
                          )}
                        </button>
                        {savedBuilders.length > 0 && (
                          <button
                            className="load-builder-btn-small"
                            onClick={() => setShowSavedBuilders(true)}
                            title="Load saved builder"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 8l-5-5-5 5M12 3v12" />
                            </svg>
                            <span className="saved-count">{savedBuilders.length}</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  <div className="bet-builder-actions-right">
                    <BookmakerLinksButton
                      fixtureId={bets[0]?.fixtureId}
                      className="place-bet-btn"
                    />
                    <button className="track-slip-btn" onClick={handleTrackSlip}>
                      Track Bet Slip
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showOddsInput && (
        <OddsInput
          selections={bets}
          onSubmit={handleTrackSubmit}
          onCancel={() => setShowOddsInput(false)}
        />
      )}
    </>
  )
}

// Keep old modal for mobile
export function BetBuilderModal() {
  const { bets, removeBet, clearAll, isModalOpen, closeModal } = useBetBuilder()
  const { addTrackedBet } = useBetTracker()
  const { getOddsForBookmaker, preferredBookmaker, formatOdds, oddsHidden } = useOdds()
  const [showOddsInput, setShowOddsInput] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape)
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isModalOpen, closeModal])

  // Helper to get odds for a single bet (with fallback to stored odds)
  const getOddsForBet = useMemo(() => {
    if (oddsHidden) return undefined
    return (bet: SavedBet): number | null => {
      // If bet has stored odds (e.g., from value bets page), use them directly
      // Must be a valid number > 1 (decimal odds are always > 1)
      if (typeof bet.odds === 'number' && bet.odds > 1) {
        return bet.odds
      }
      // Otherwise try to get from OddsContext (for fixture page bets)
      if (preferredBookmaker) {
        const oddsMarketSlug = angleMarketToOddsMarket(bet.marketType, bet.teamType)
        if (oddsMarketSlug) {
          const outcome = bet.isOver ? 'over' : 'under'
          const contextOdds = getOddsForBookmaker(
            bet.fixtureId,
            oddsMarketSlug,
            outcome,
            preferredBookmaker.slug,
            bet.line
          )
          if (contextOdds !== null && contextOdds > 1) return contextOdds
        }
      }
      return null
    }
  }, [preferredBookmaker, getOddsForBookmaker, oddsHidden])

  // Calculate combined odds from all bets
  const combinedOddsInfo = useMemo(() => {
    if (bets.length === 0) return null
    if (!getOddsForBet) return null

    const oddsValues: number[] = []
    let allOddsAvailable = true

    for (const bet of bets) {
      const odds = getOddsForBet(bet)
      if (odds !== null) {
        oddsValues.push(odds)
      } else {
        allOddsAvailable = false
      }
    }

    if (oddsValues.length === 0) return null

    const combined = calculateCombinedOdds(oddsValues)
    return {
      combined,
      count: oddsValues.length,
      total: bets.length,
      allAvailable: allOddsAvailable,
    }
  }, [bets, getOddsForBet])

  const handleTrackSlip = () => {
    setShowOddsInput(true)
  }

  const handleShare = async () => {
    const text = formatBetSlip(bets)
    // Try Web Share API first (mobile)
    if (!shareViaWebShare(text)) {
      // Fall back to clipboard copy
      const success = await copyToClipboard(text)
      if (success) {
        setCopySuccess(true)
        setTimeout(() => setCopySuccess(false), 2000)
      }
    }
  }

  const handleTrackSubmit = (oddsDecimal: number, stake: number) => {
    const trackedSlip: TrackedBetSlip = {
      id: `slip-${Date.now()}`,
      selections: [...bets],
      oddsDecimal,
      stake,
      status: 'pending',
      placedAt: new Date().toISOString(),
    }

    addTrackedBet(trackedSlip)
    clearAll()
    setShowOddsInput(false)
    closeModal()
  }

  return (
    <>
      <div className={`bet-builder-modal-mobile ${isModalOpen ? 'open' : ''}`}>
        <div className="modal-handle" />
        <div className="modal-header">
          <div className="row">
            <span className="modal-title">My Bet Builder</span>
            <span className="bet-count">{bets.length} pick{bets.length !== 1 ? 's' : ''}</span>
          </div>
          <button className="modal-close" onClick={closeModal}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          {bets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <div className="empty-text">
                No picks yet.<br />
                Add angles from fixtures to build your bet slip.
              </div>
            </div>
          ) : (
            <>
              <GroupedBetList
                bets={bets}
                removeBet={removeBet}
                getOddsForBet={getOddsForBet}
                formatOdds={formatOdds}
              />
              {!oddsHidden && combinedOddsInfo && (
                <div
                  className="combined-odds"
                  title={`Combined accumulator odds: ${combinedOddsInfo.combined.toFixed(2)} decimal (implies ${(100 / combinedOddsInfo.combined).toFixed(2)}% chance)`}
                >
                  <span className="combined-odds-label">
                    Acca Odds ({combinedOddsInfo.count}/{combinedOddsInfo.total})
                  </span>
                  <span className="combined-odds-value">
                    {formatOdds(combinedOddsInfo.combined)}
                  </span>
                </div>
              )}
              <div className="bet-builder-footer">
                <button className="share-btn" onClick={handleShare} title={copySuccess ? 'Copied!' : 'Copy bet slip'}>
                  {copySuccess ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  )}
                  {copySuccess ? 'Copied!' : 'Share'}
                </button>
                <button className="track-slip-btn" onClick={handleTrackSlip}>
                  Track Bet Slip
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showOddsInput && (
        <OddsInput
          selections={bets}
          onSubmit={handleTrackSubmit}
          onCancel={() => setShowOddsInput(false)}
        />
      )}
    </>
  )
}
